import React, { useEffect } from 'react';
import { X, Save, Plus, BookOpen, Calendar, Clock, User, Award } from 'lucide-react';
import FormInput from '../FormInput';

const EvaluationForm = ({
  showModal,
  setShowModal,
  formData,
  setFormData,
  selectedClasse,
  setSelectedClasse,
  selectedModule,
  setSelectedModule,
  classes,
  modules,
  handleSubmit,
  resetForm,
  isEditing,
  loadModulesByClasse
}) => {

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClasseChange = (classeId) => {
    setSelectedClasse(classeId);
    setFormData(prev => ({
      ...prev,
      classe: classeId
    }));
    loadModulesByClasse(classeId);
    setSelectedModule('');
    setFormData(prev => ({
      ...prev,
      module: ''
    }));
  };

  const handleModuleChange = (moduleId) => {
    setSelectedModule(moduleId);
    setFormData(prev => ({
      ...prev,
      module: moduleId
    }));
  };



  // Fonction utilitaire pour construire les options
  const buildOptions = (items, valueKey = 'id', labelKey = 'libelle', defaultLabel = 'Sélectionner...') => {
    const options = [{ value: '', label: defaultLabel }];
    
    // Ajouter toutes les options disponibles
    options.push(...items.map(item => {
      let label = 'Non défini';
      
      // Gérer différentes structures de données
      if (labelKey === 'libelle') {
        if (item.libelle) {
          label = item.libelle;
        } else if (item.module && item.module.libelle) {
          // Structure du ModuleClasseSerializer
          label = item.module.libelle;
        } else if (item.module_libelle) {
          label = item.module_libelle;
        }
      } else {
        label = item[labelKey] || 'Non défini';
      }
      
      return {
        value: item[valueKey],
        label: label
      };
    }));
    
    return options;
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };

  // Effet pour charger les modules quand une classe est sélectionnée
  useEffect(() => {
    if (selectedClasse) {
      loadModulesByClasse(selectedClasse);
    }
  }, [selectedClasse, loadModulesByClasse]);

  // Debug : Log des données disponibles (commenté pour la prod)
  // useEffect(() => {
  //   console.log('🔍 FormData:', formData);
  //   console.log('🔍 Classes disponibles:', classes);
  //   console.log('🔍 Modules disponibles:', modules);
  //   console.log('🔍 Selected classe:', selectedClasse);
  //   console.log('🔍 Selected module:', selectedModule);
  // }, [formData, classes, modules, selectedClasse, selectedModule]);

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Modifier l\'évaluation' : 'Nouvelle évaluation'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

                {/* Form */}
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="space-y-4">
            {/* Section 1: Type et Date */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <Award className="h-4 w-4 mr-2 text-[#202654]" />
                Informations générales
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Type d'évaluation */}
                <div className="mb-2">
                  <FormInput
                    label="Type d'évaluation"
                    type="select"
                    name="libelle"
                    value={formData.libelle}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: 'Sélectionner un type' },
                      { value: 'Devoir', label: 'Devoir' },
                      { value: 'Interrogation', label: 'Interrogation' },
                      { value: 'Expose', label: 'Exposé' }
                    ]}
                    required
                    icon={Award}
                    className="mb-0"
                  />
                </div>

                {/* Date */}
                <div className="mb-2">
                  <FormInput
                    label="Date du contrôle"
                    type="date"
                    name="date_controle"
                    value={formData.date_controle}
                    onChange={handleInputChange}
                    required
                    icon={Calendar}
                    className="mb-0"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Horaires */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <Clock className="h-4 w-4 mr-2 text-[#202654]" />
                Horaires
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Heure début */}
                <div className="mb-2">
                  <FormInput
                    label="Heure de début"
                    type="time"
                    name="heure_debut"
                    value={formData.heure_debut}
                    onChange={handleInputChange}
                    required
                    icon={Clock}
                    className="mb-0"
                  />
                </div>

                {/* Heure fin */}
                <div className="mb-2">
                  <FormInput
                    label="Heure de fin"
                    type="time"
                    name="heure_fin"
                    value={formData.heure_fin}
                    onChange={handleInputChange}
                    required
                    icon={Clock}
                    className="mb-0"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Classe et Module */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <User className="h-4 w-4 mr-2 text-[#202654]" />
                Classe et matière
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Classe */}
                <div className="mb-2">
                  <FormInput
                    label="Classe"
                    type="select"
                    name="classe"
                    value={selectedClasse}
                    onChange={(e) => handleClasseChange(e.target.value)}
                    options={buildOptions(classes, 'id', 'libelle', 'Sélectionner une classe')}
                    required
                    icon={User}
                    className="mb-0"
                  />
                </div>

                {/* Module */}
                <div className="mb-2">
                  <FormInput
                    label="Module"
                    type="select"
                    name="module"
                    value={selectedModule}
                    onChange={(e) => handleModuleChange(e.target.value)}
                    options={buildOptions(modules, 'id', 'libelle', selectedClasse ? 'Sélectionner un module' : 'Sélectionner d\'abord une classe')}
                    disabled={!selectedClasse}
                    required
                    icon={BookOpen}
                    helpText={!selectedClasse ? "Sélectionnez d'abord une classe" : modules.length === 0 && selectedClasse ? "Chargement des modules..." : undefined}
                    className="mb-0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
            >
              {isEditing ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Modifier
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EvaluationForm; 